import React, { useState, useEffect } from 'react';
import TaskRow from './TaskRow';
import { isOverdue } from '../lib/utils';

const DOMAIN_COLORS = {
  tech:   { bg:'rgba(14,165,233,.1)',   color:'#0284C7' },
  mkt:    { bg:'rgba(244,114,182,.12)', color:'#BE185D' },
  design: { bg:'rgba(168,85,247,.12)',  color:'#7C3AED' },
  ops:    { bg:'rgba(245,158,11,.12)',  color:'#B45309' },
  strat:  { bg:'rgba(99,102,241,.12)', color:'#4338CA' },
  fin:    { bg:'rgba(16,185,129,.12)', color:'#047857' },
};

export default function DomainSection({ domain, tasks, allTasks, month, onEdit, onCycle, onDelete, onAddTask }) {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const domainAll    = allTasks.filter(t => t.domain === domain.id && t.month === month);
  const done         = domainAll.filter(t => t.status === 'done').length;
  const wip          = domainAll.filter(t => t.status === 'in_progress' && !isOverdue(t)).length;
  const overdueCount = domainAll.filter(t => isOverdue(t)).length;
  const pct          = domainAll.length ? Math.round((done / domainAll.length) * 100) : 0;
  const dcol         = DOMAIN_COLORS[domain.id] || { bg:'#F1F5F9', color:'#3D5878' };

  return (
    <section style={styles.section}>
      <header
        style={{ ...styles.header, padding: isMobile ? '14px 16px' : '18px 22px', gap: isMobile ? 10 : 14 }}
        onClick={() => setCollapsed(c => !c)}
        onMouseEnter={e => e.currentTarget.style.background = '#F7FAFD'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ ...styles.icon, background: dcol.bg, color: dcol.color, width: isMobile ? 32 : 38, height: isMobile ? 32 : 38 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={isMobile ? 15 : 18} height={isMobile ? 15 : 18}
            dangerouslySetInnerHTML={{ __html: domain.icon }} />
        </div>

        <div style={styles.meta}>
          <div style={{ ...styles.domainTitle, fontSize: isMobile ? 16 : 20, flexWrap: 'wrap', gap: isMobile ? 6 : 10 }}>
            {domain.name}
            <span style={styles.lead}>Lead · {domain.lead}</span>
          </div>
          {!isMobile && (
            <div style={styles.stats}>
              <span style={styles.statPill}><strong>{domainAll.length}</strong> tasks</span>
              <span style={styles.statPill}><span style={{ ...styles.dot, background:'#10B981' }}/><strong>{done}</strong> done</span>
              <span style={styles.statPill}><span style={{ ...styles.dot, background:'#F59E0B' }}/><strong>{wip}</strong> in progress</span>
              {overdueCount > 0 && <span style={styles.statPill}><span style={{ ...styles.dot, background:'#EF4444' }}/><strong>{overdueCount}</strong> overdue</span>}
            </div>
          )}
          {isMobile && (
            <div style={{ ...styles.stats, gap:8, marginTop:4, fontSize:11 }}>
              <span style={styles.statPill}><strong>{domainAll.length}</strong> tasks</span>
              <span style={styles.statPill}><span style={{ ...styles.dot, background:'#10B981' }}/><strong>{done}</strong> done</span>
              {overdueCount > 0 && <span style={{ ...styles.statPill, color:'#EF4444' }}><span style={{ ...styles.dot, background:'#EF4444' }}/><strong>{overdueCount}</strong> overdue</span>}
            </div>
          )}
        </div>

        {!isMobile && (
          <div style={styles.progressWrap}>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width:`${pct}%` }} />
            </div>
          </div>
        )}

        <div style={{ ...styles.chev, transform: collapsed ? 'rotate(-90deg)' : 'none', transition:'transform .25s ease' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </header>

      {!collapsed && (
        <div style={styles.body}>
          <div style={{ ...styles.addRow, padding: isMobile ? '10px 16px 4px' : '12px 22px 6px' }}>
            <button
              onClick={e => { e.stopPropagation(); onAddTask(domain.id); }}
              style={styles.addBtn}
              onMouseEnter={e => e.currentTarget.style.background='#E6F4FB'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="13" height="13">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Add task to {domain.name}
            </button>
          </div>

          <div style={{ ...styles.taskList, padding: isMobile ? '4px 8px 12px' : '4px 14px 16px' }}>
            {tasks.length === 0 ? (
              <div style={styles.empty}>
                No tasks match.{' '}
                <button
                  onClick={e => { e.stopPropagation(); onAddTask(domain.id); }}
                  style={{ background:'none', border:'none', color:'#0284C7', cursor:'pointer', fontWeight:500, fontSize:13 }}
                >
                  Add one
                </button>
              </div>
            ) : tasks.map(t => (
              <TaskRow key={t.id} task={t} onEdit={onEdit} onCycle={onCycle} onDelete={onDelete} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const styles = {
  section: { background:'white', border:'1px solid #E4ECF4', borderRadius:20, marginBottom:14, overflow:'hidden', transition:'box-shadow .2s ease' },
  header: { display:'flex', alignItems:'center', cursor:'pointer', userSelect:'none', transition:'background .15s ease' },
  icon: { borderRadius:10, display:'grid', placeItems:'center', flexShrink:0 },
  meta: { flex:1, minWidth:0 },
  domainTitle: { fontFamily:"'Fraunces',serif", fontWeight:500, letterSpacing:'-0.02em', lineHeight:1.1, display:'flex', alignItems:'center' },
  lead: { fontSize:10, fontFamily:"'Geist',sans-serif", fontWeight:500, color:'#7A8BA3', textTransform:'uppercase', letterSpacing:'0.1em', padding:'2px 7px', background:'#F1F5F9', borderRadius:99, flexShrink:0 },
  stats: { marginTop:5, fontSize:12, color:'#3D5878', display:'flex', alignItems:'center', flexWrap:'wrap', gap:10 },
  statPill: { display:'inline-flex', alignItems:'center', gap:5 },
  dot: { width:7, height:7, borderRadius:'50%', display:'inline-block' },
  progressWrap: { width:120, flexShrink:0 },
  progressTrack: { height:5, background:'#EEF3F8', borderRadius:99, overflow:'hidden' },
  progressFill: { height:'100%', background:'linear-gradient(90deg,#0EA5E9,#10B981)', borderRadius:99, transition:'width .6s ease' },
  chev: { width:28, height:28, display:'grid', placeItems:'center', borderRadius:8, color:'#7A8BA3', flexShrink:0 },
  body: { borderTop:'1px solid #EEF3F8', background:'linear-gradient(180deg,#FBFCFE,#FFF)' },
  addRow: { display:'flex', justifyContent:'flex-end' },
  addBtn: { fontSize:12, color:'#0284C7', fontWeight:500, display:'inline-flex', alignItems:'center', gap:5, padding:'6px 10px', borderRadius:8, transition:'all .15s ease', background:'transparent', border:'none', cursor:'pointer' },
  taskList: {},
  empty: { padding:'18px 14px', color:'#7A8BA3', fontSize:13, fontStyle:'italic' },
};
